import { CButton } from "components/UI/Button/Buttons";
import tweekRoundedLogo from "assets/svg/tweekLogoWithRoundedBorder.svg";
import btnIcon from "assets/svg/onboardingSessionButtonIcon.svg";
import btnIcon2 from "assets/svg/onboardingSessionBtnIcon2.svg";
import arrowBack from "assets/svg/arrow-left-dark.svg";
import onboardingLine from "assets/svg/onbordingBgLine1.svg";
import profileIcon from "assets/svg/profileOrange.svg";
import preferenceIcon from "assets/svg/preferenceOnboarding.svg";
import preferenceIconOrange from "assets/svg/preferenceOnboardingIconOrange.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import RightSideonboardingContainer from "./RightSideonboardingContainer";
import OnbordingFirstMainBody from "./OnbordingFirstMainBody";
import OnboardingSecondMainBody from "./OnboardingSecondMainBody";
import { useNavigate } from "react-router-dom";
import { Col, Modal, Row } from "antd";
import useDocumentTitle from "hooks/useDocumentTitle";
import logo from "assets/svg/fullLogo.svg";
import {
  useUpdateAccount,
  useGetMyProfile,
  useGetProfessionalDescription,
} from "hooks/query/useAccount";
import { UpdateAccountServicePayload } from "interfaces";
import { getUserDetails, isTokenValid } from "utils/localStorageServices";
import { setAuthCookies } from "utils/cookieService";
import { readRedirectParam } from "utils/redirectPath";

export default function FirstOnboardingSessionPage({
  isModal,
  open,
  handleClose,
}: {
  isModal?: boolean;
  open?: boolean;
  handleClose?: () => void;
}) {
  const [isFirstPage, setIsFirstPage] = useState<boolean>(true);
  const [formData, setFormData] = useState<UpdateAccountServicePayload>({});
  const [selectedUsageTags, setSelectedUsageTags] = useState<string[]>([]);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const hasPrefilled = useRef(false);
  // Destination the user was headed to before signing up (e.g.
  // /checkout/presenter when they came from the landing page). Captured on
  // mount so it survives any URL changes during onboarding.
  const redirectAfterOnboarding = useRef(readRedirectParam());

  useEffect(() => {
    return () => {
      setIsFirstPage(true);
      setFormData({});
      setSelectedUsageTags([]);
      hasPrefilled.current = false;
    };
  }, []);

  const navigate = useNavigate();
  useDocumentTitle("Onboarding - StagePanda", !isModal);

  const hasValidAuth = !!getUserDetails() && isTokenValid();

  const { data: profileData } = useGetMyProfile(hasValidAuth);
  const { data: professionalDescriptions } =
    useGetProfessionalDescription(hasValidAuth && (!isModal || !!open));

  // Prefill form from profile data when it arrives
  useEffect(() => {
    if (!profileData || hasPrefilled.current) return;
    hasPrefilled.current = true;

    setFormData((prev) => {
      // Don't overwrite fields the user has already filled
      if (Object.keys(prev).length > 0) return prev;

      const prefill: Partial<UpdateAccountServicePayload> = {};

      if (
        profileData.fullName &&
        profileData.fullName !== "NA" &&
        profileData.fullName !== "na"
      ) {
        prefill.FullName = profileData.fullName;
      }

      if (profileData.location) {
        prefill.Location = profileData.location;
      }

      if (profileData.jobTitle) {
        prefill.JobTitle = profileData.jobTitle;
      }

      if (profileData.jobId) {
        prefill.JobId = profileData.jobId;
      }

      if (profileData.linkedIn) {
        prefill.LinkedIn = profileData.linkedIn;
      }

      if (profileData.locationShort) {
        prefill.LocationShort = profileData.locationShort;
      }

      // Check URL params for country (overrides profile data)
      const params = new URLSearchParams(window.location.search);
      const encodedCountry = params.get("c");
      if (encodedCountry) {
        try {
          const decodedData = JSON.parse(atob(encodedCountry));
          if (decodedData.country) {
            prefill.Location = decodedData.country;
          }
        } catch (error) {
          console.error("Error decoding country data:", error);
        }
      }

      if (Object.keys(prefill).length > 0) {
        return { ...prev, ...prefill };
      }
      return prev;
    });
  }, [profileData]);

  const onSuccess = useCallback(() => {
    const userDetails = getUserDetails();
    if (userDetails) {
      const updatedDetails = {
        ...userDetails,
        fullName: formDataRef.current.FullName,
      };
      setAuthCookies(updatedDetails);
      localStorage.setItem("user", JSON.stringify(updatedDetails));
    }
    localStorage.setItem("onboardingComplete", "true");
    if (isModal && handleClose) {
      handleClose();
    } else {
      navigate(redirectAfterOnboarding.current || "/overview");
    }
  }, [isModal, handleClose, navigate]);

  const { mutate: updateAccount, isPending: isSubmitting } =
    useUpdateAccount(onSuccess);

  const handleSubmit = (usageTags: string[]) => {
    const payload: UpdateAccountServicePayload = {
      ...formData,
      UsageTags: JSON.stringify(usageTags),
    };
    updateAccount(payload);
  };

  if (isModal && open !== undefined && handleClose) {
    return (
      <Modal
        open={open}
        onCancel={() => handleClose && handleClose()}
        footer={null}
        width={"fit-content"}
        centered
        destroyOnClose={true}
        closable={false}
        styles={{
          body: {
            padding: 0,
            borderRadius: "32px",
            paddingTop: "30px",
          },
          content: {
            padding: 0,
            borderRadius: "32px",
          },
        }}
      >
        <Row className={"w-full"} justify={"center"}>
          <Col lg={18} xs={22}>
            {/* <Row className={"w-full"} justify={"center"} align={"middle"}> */}
            <Row className={"w-full"} justify={"center"}>
              <Col className={"hidden lg:block"} lg={12} xs={24}>
                <Row justify={"center"}>
                  <Col lg={16} xs={24} className="">
                    <div className={"mb-8"}>
                      <img src={logo} alt="" />
                    </div>
                    <div className="">
                      <p className="font-grotesk text-3xl leading-snug font-semibold">
                        Welcome,{" "}
                        <span className="text-[#FF6929]">Presenter!</span> Just a
                        few more steps to go.
                      </p>
                      <p className="font-sfRegular text-base mt-2 mb-8 text-[#344054]">
                        Complete the setup to unlock your full experience
                      </p>
                    </div>
                    <div>
                      <div className="flex  mb-0 gap-2">
                        <div className="flex mt-2 items-center flex-col">
                          <img src={profileIcon} alt="" />
                          <div
                            className={`w-[2px] ${
                              isFirstPage ? `bg-[#ACB2BD]` : `bg-[#FF6929]`
                            } h-16`}
                          ></div>
                        </div>
                        <div>
                          <p className="text-base font-sfRegular text-[#FF6929]">
                            Personal profile
                          </p>
                          <p className="text-sm text-[#545454] font-sfRegular">
                            Tell us about yourself
                          </p>
                        </div>
                      </div>
                      <div className="flex mt-0  gap-2">
                        <div className="flex items-center flex-col">
                          <img
                            src={
                              isFirstPage
                                ? preferenceIcon
                                : preferenceIconOrange
                            }
                            alt=""
                          />
                        </div>
                        <div>
                          <p
                            className={`text-base font-sfRegular ${
                              isFirstPage ? `text-[#ACB2BD]` : `text-[#FF6929]`
                            }`}
                          >
                            Usage and preference
                          </p>
                          <p className="text-sm text-[#545454] font-sfRegular">
                            What do you want to do with stagepanda?
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex mb-4 lg:mb-0 justify-between mt-8 flex-wrap gap-2">
                      {!isFirstPage && (
                        <CButton
                          variant="transparentWithGrayBorder"
                          iconPosition="left"
                          text="Back"
                          icon={arrowBack}
                          action={() => setIsFirstPage(true)}
                        />
                      )}
                      <CButton
                        text="Complete setup later"
                        icon={isFirstPage ? btnIcon : btnIcon2}
                        action={() => {
                          if (isModal) {
                            handleClose();
                          } else {
                            navigate(`/overview`);
                          }
                        }}
                        variant="transparentWithGrayBorder"
                      />
                    </div>
                  </Col>
                </Row>
              </Col>
              <Col lg={12} xs={24}>
                <Row
                  className={"block lg:hidden"}
                  justify={"center"}
                  align={"middle"}
                >
                  <Col xs={24}>
                    <div className="grid grid-cols-1 gap-5 mb-8">
                      <Row justify={"center"} align={"middle"}>
                        <img src={tweekRoundedLogo} alt="" />
                      </Row>

                      <div className="grid grid-cols-1 gap-2">
                        <p className="font-grotesk font-medium text-xl">
                          Welcome,{" "}
                          <span className="text-[#FF6929]">Tweekster!</span>{" "}
                          Just a few more steps to go.
                        </p>
                        <p className="text-[#545454] font-sfRegular text-sm">
                          Complete the setup to unlock your full experience
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row justify={"center"} align={"middle"}>
                  <Col xs={24}>
                    <RightSideonboardingContainer isFirstPage={isFirstPage}>
                      {isFirstPage ? (
                        <OnbordingFirstMainBody
                          setIsFirstPage={() => setIsFirstPage(false)}
                          formData={formData}
                          setFormData={setFormData}
                          profileData={profileData}
                          professionalDescriptions={professionalDescriptions}
                        />
                      ) : (
                        <OnboardingSecondMainBody
                          onSubmit={handleSubmit}
                          isSubmitting={isSubmitting}
                          selectedValues={selectedUsageTags}
                          setSelectedValues={setSelectedUsageTags}
                        />
                      )}
                    </RightSideonboardingContainer>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    );
  }

  return (
    <Row
      justify={"center"}
      // align={"middle"}
      style={{
        backgroundImage: `url(${onboardingLine})`,
      }}
      className="flex bg-[#F6F3EF] min-h-screen bg-no-repeat py-[53px]"
    >
      {/* <Row className={"w-full"} justify={"center"} align={"middle"}> */}
      <Row className={"w-full"} justify={"center"}>
        <Col lg={18} xs={22}>
          {/* <Row className={"w-full"} justify={"center"} align={"middle"}> */}
          <Row className={"w-full"} justify={"center"}>
            <Col className={"hidden lg:block"} lg={12} xs={24}>
              <Row justify={"center"}>
                <Col lg={16} xs={24} className="">
                  <div className={"mb-8"}>
                    <img src={logo} alt="" />
                  </div>
                  <div className="">
                    <p className="font-grotesk text-3xl leading-snug font-semibold">
                      Welcome, <span className="text-[#FF6929]">Presenter!</span>{" "}
                      Just a few more steps to go.
                    </p>
                    <p className="font-sfRegular text-base mt-2 mb-8 text-[#344054]">
                      Complete the setup to unlock your full experience
                    </p>
                  </div>
                  <div>
                    <div className="flex  mb-0 gap-2">
                      <div className="flex mt-2 items-center flex-col">
                        <img src={profileIcon} alt="" />
                        <div
                          className={`w-[2px] ${
                            isFirstPage ? `bg-[#ACB2BD]` : `bg-[#FF6929]`
                          } h-16`}
                        ></div>
                      </div>
                      <div>
                        <p className="text-base font-sfRegular text-[#FF6929]">
                          Personal profile
                        </p>
                        <p className="text-sm text-[#545454] font-sfRegular">
                          Tell us about yourself
                        </p>
                      </div>
                    </div>
                    <div className="flex mt-0  gap-2">
                      <div className="flex items-center flex-col">
                        <img
                          src={
                            isFirstPage ? preferenceIcon : preferenceIconOrange
                          }
                          alt=""
                        />
                      </div>
                      <div>
                        <p
                          className={`text-base font-sfRegular ${
                            isFirstPage ? `text-[#ACB2BD]` : `text-[#FF6929]`
                          }`}
                        >
                          Usage and preference
                        </p>
                        <p className="text-sm text-[#545454] font-sfRegular">
                          What do you want to do with stagepanda?
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex mb-4 lg:mb-0 justify-between mt-8">
                    {!isFirstPage && (
                      <CButton
                        variant="transparentWithGrayBorder"
                        iconPosition="left"
                        text="Back"
                        icon={arrowBack}
                        action={() => setIsFirstPage(true)}
                      />
                    )}
                    {/* <CButton
                      text="Complete setup later"
                      icon={isFirstPage ? btnIcon : btnIcon2}
                      action={() => navigate(`/overview`)}
                      variant="transparentWithGrayBorder"
                    /> */}
                  </div>
                </Col>
              </Row>
            </Col>
            <Col lg={12} xs={24}>
              <Row
                className={"block lg:hidden"}
                justify={"center"}
                align={"middle"}
              >
                <Col xs={24}>
                  <div className="grid grid-cols-1 gap-5 mb-8">
                    <Row justify={"center"} align={"middle"}>
                      <img src={tweekRoundedLogo} alt="" />
                    </Row>

                    <div className="grid grid-cols-1 gap-2">
                      <p className="font-grotesk font-medium text-xl">
                        Welcome,{" "}
                        <span className="text-[#FF6929]">Tweekster!</span> Just
                        a few more steps to go.
                      </p>
                      <p className="text-[#545454] font-sfRegular text-sm">
                        Complete the setup to unlock your full experience
                      </p>
                    </div>

                    <div className="flex mb-2 lg:mb-0 justify-between mt-2">
                      {!isFirstPage && (
                        <CButton
                          variant="transparentWithGrayBorder"
                          iconPosition="left"
                          text="Back"
                          icon={arrowBack}
                          action={() => setIsFirstPage(true)}
                        />
                      )}
                      {/* <CButton
                      text="Complete setup later"
                      icon={isFirstPage ? btnIcon : btnIcon2}
                      action={() => navigate(`/overview`)}
                      variant="transparentWithGrayBorder"
                    /> */}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row justify={"center"} align={"middle"}>
                <Col xs={24}>
                  <RightSideonboardingContainer isFirstPage={isFirstPage}>
                    {isFirstPage ? (
                      <OnbordingFirstMainBody
                        setIsFirstPage={() => setIsFirstPage(false)}
                        formData={formData}
                        setFormData={setFormData}
                        profileData={profileData}
                        professionalDescriptions={professionalDescriptions}
                      />
                    ) : (
                      <OnboardingSecondMainBody
                        onSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        selectedValues={selectedUsageTags}
                        setSelectedValues={setSelectedUsageTags}
                      />
                    )}
                  </RightSideonboardingContainer>
                </Col>
              </Row>
            </Col>
          </Row>
        </Col>
      </Row>
    </Row>
  );
}
